import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface FormationAdmin {
  id: string;
  title: string;
  slug: string;
  description: string;
  category: string;
  status: 'active' | 'inactive' | 'draft';
  duration_days: number;
  duration_hours: number;
  price_ht: number;
  price_ttc: number;
  max_participants: number;
  min_participants: number;
  lang: string | null;
  published: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface FormationsFilters {
  status?: 'all' | 'active' | 'inactive' | 'draft';
  category?: string;
  lang?: string;
  search?: string;
}

// Récupérer toutes les formations (admin, y compris drafts)
export function useFormationsAdmin(filters: FormationsFilters = {}) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['admin', 'formations', filters],
    queryFn: async () => {
      let query = supabase
        .from('formations')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters.category) {
        query = query.eq('category', filters.category);
      }
      if (filters.lang) {
        query = query.eq('lang', filters.lang);
      }
      if (filters.search) {
        query = query.ilike('title', `%${filters.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as FormationAdmin[];
    },
    enabled: !!user,
  });
}

// Statistiques formations
export function useFormationsStats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['admin', 'formations', 'stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('formations')
        .select('*');

      if (error) throw error;

      const formations = data as FormationAdmin[];
      return {
        total: formations.length,
        active: formations.filter((f) => f.status === 'active').length,
        draft: formations.filter((f) => f.status === 'draft').length,
        inactive: formations.filter((f) => f.status === 'inactive').length,
      };
    },
    enabled: !!user,
  });
}

// Catégories uniques des formations
export function useFormationCategories() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['admin', 'formations', 'categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('formations')
        .select('category');

      if (error) throw error;

      const categories = [...new Set((data || []).map((f) => f.category))].filter(Boolean);
      return categories.sort();
    },
    enabled: !!user,
  });
}
